import {
    AccessLevel,
    Account as AccountPayload,
    AccountCore,
    AccountOverview,
    AccountWarnings,
    AdvancedOptions,
    BrandingInfo,
    CrcDecision,
    CrcFormPayload,
    CrcProfile,
    CRCStatus,
    LoginPayload,
    MarketingOptions,
    NewAccountPayload,
} from '@dev/ee-api/ee-api-models';

import * as Types from './types';
import { ApplicationState } from '..';

const { AccountActionTypes } = Types;

export const AccountStartLoading = (loading = true): Types.AccountFetchStartAction => ({
    type: AccountActionTypes.FETCH_START,
    payload: {
        accountLoading: loading,
    },
});

export const AccountLoadingError = (): Types.AccountFetchErrorAction => ({
    type: AccountActionTypes.FETCH_ERROR,
    payload: {
        accountLoading: false,
    },
});

export const AccountLoadingSuccess = (account: AccountPayload): Types.AccountFetchSuccessAction => ({
    type: AccountActionTypes.FETCH_SUCCESS,
    payload: {
        account,
        accountLoading: false,
    },
});

export const AccountStartUpdate = (option: keyof AdvancedOptions, value: string | boolean | number): Types.AccountUpdateStartAction => ({
    type: AccountActionTypes.UPDATE_START,
    payload: {
        option,
        value,
        accountLoading: false,
    },
    meta: {
        thunk: true,
    },
});
export const AccountUpdateGlobalContacts = (globalcontacts: boolean): Types.AccountUpdateGlobalContactsAction => ({
    type: AccountActionTypes.UPDATE_GLOBAL_CONTACTS,
    payload: {
        globalcontacts,
    },
    meta: {
        thunk: true,
    },
});

export const AccountSuccessUpdate = (account: AccountPayload, meta: Record<string, unknown>): Types.AccountUpdateSuccessAction => ({
    type: AccountActionTypes.UPDATE_SUCCESS,
    payload: {
        account,
        accountLoading: false,
    },
    meta,
});
export const AccountErrorUpdate = (
    failedOption: Record<string, string | boolean>,
    meta: Record<string, unknown>
): Types.AccountUpdateErrorAction => ({
    type: AccountActionTypes.UPDATE_ERROR,
    payload: failedOption,
    error: true,
    meta,
});
export const AccountUploadLogo = (file: File): Types.AccountUploadLogoAction => ({
    type: AccountActionTypes.UPLOAD_LOGO_START,
    payload: {
        file,
    },
});
export const AccountDeleteLogo = (fileName: string): Types.AccountDeleteLogoAction => ({
    type: AccountActionTypes.DELETE_LOGO,
    payload: {
        fileName,
    },
});
export const AccountUploadLogoFinish = (): Types.AccountFinishUploadLogoAction => ({
    type: AccountActionTypes.UPLOAD_LOGO_FINISH,
});

export const CreateAccount = (newAccountParams: NewAccountPayload): Types.CreateAccountAction => ({
    type: AccountActionTypes.CREATE_ACCOUNT,
    payload: newAccountParams,
    meta: {
        thunk: true,
    },
});

export const CreateAccountStart = (): Types.CreateAccountStartAction => ({
    type: AccountActionTypes.CREATE_ACCOUNT_START,
});

export const CreateAccountWithGoogle = (newAccountParams: LoginPayload): Types.CreateAccountWithGoogleAction => ({
    type: AccountActionTypes.CREATE_ACCOUNT_WITH_GOOGLE,
    payload: newAccountParams,
    meta: {
        thunk: true,
    },
});

export const CreateAccountWithGoogleSuccess = (meta: Record<string, unknown>): Types.CreateAccountWithGoogleSuccessAction => ({
    type: AccountActionTypes.CREATE_ACCOUNT_WITH_GOOGLE_SUCCESS,
    payload: {
        createAccountInProgress: false,
    },
    meta,
});

export const CreateAccountSuccess = (account: AccountPayload, meta: Record<string, unknown>): Types.CreateAccountSuccessAction => ({
    type: AccountActionTypes.CREATE_ACCOUNT_SUCCESS,
    payload: {
        GeneralInfo: account.GeneralInfo,
        Profile: account.Profile,
        Overview: account.Overview,
        AdvancedOptions: account.AdvancedOptions,
        InboundOptions: account.InboundOptions,
        Reputation: account.Reputation,
        IC: account.IC,
        createAccountInProgress: false,
    },
    meta,
});

export const CreateAccountError = (error: Record<string, unknown>, meta: Record<string, unknown>): Types.CreateAccountErrorAction => ({
    type: AccountActionTypes.CREATE_ACCOUNT_ERROR,
    payload: error,
    meta,
    error: true,
});

export const ResetPassword = (email: string): Types.ResetPasswordAction => ({
    type: AccountActionTypes.RESET_PASSWORD,
    payload: { email },
    meta: {
        thunk: true,
    },
});

export const ResetPasswordStart = (): Types.ResetPasswordStartAction => ({
    type: AccountActionTypes.RESET_PASSWORD_START,
});

export const ResetPasswordSuccess = (meta: Record<string, unknown>): Types.ResetPasswordSuccessAction => ({
    type: AccountActionTypes.RESET_PASSWORD_SUCCESS,
    meta,
});

export const ResetPasswordError = (error: Record<string, unknown>, meta: Record<string, unknown>): Types.ResetPasswordErrorAction => ({
    type: AccountActionTypes.RESET_PASSWORD_ERROR,
    payload: error,
    error: true,
    meta,
});

export const ResetPasswordUser = (email: string, previousMeta: Record<string, unknown>): Types.ResetPasswordUserAction => ({
    type: AccountActionTypes.RESET_PASSWORD_USER,
    payload: { email },
    previousMeta,
    meta: {
        thunk: true,
    },
});

export const ValidateInput = (option: Record<string, string>, type: Types.ValidationType): Types.ValidateInputAction => ({
    type: AccountActionTypes.VALIDATE_INPUT,
    payload: {
        option,
        type,
    },
});
export const ValidateInputError = (optionName: string): Types.ValidateInputErrorAction => ({
    type: AccountActionTypes.VALIDATE_INPUT_ERROR,
    payload: {
        optionName,
    },
});
export const ValidateInputSuccess = (): Types.ValidateInputSuccessAction => ({
    type: AccountActionTypes.VALIDATE_INPUT_SUCCESS,
});

export const ResetAccountEmail = (verificationKey: string, newEmail: string): Types.ResetAccountEmailAction => ({
    type: AccountActionTypes.RESET_ACCOUNT_EMAIL_START,
    payload: {
        verificationKey,
        newEmail,
    },
    meta: {
        thunk: true,
    },
});
export const ResetAccountEmailSuccess = (meta: Record<string, unknown>): Types.ResetAccountEmailSuccessAction => ({
    type: AccountActionTypes.RESET_ACCOUNT_EMAIL_SUCCESS,
    meta,
});
export const ResetAccountEmailError = (meta: Record<string, unknown>): Types.ResetAccountEmailErrorAction => ({
    type: AccountActionTypes.RESET_ACCOUNT_EMAIL_ERROR,
    meta,
    error: true,
});

export const LoginWithDisposableToken = ({
    productCode,
    isBilling,
    dashboardUrl,
    accountDashPathName,
    goToLegacyDash,
}: Types.LoginWithDisposableTokenPayload): Types.LoginWithDisposbaleTokenAction => ({
    type: AccountActionTypes.LOGIN_WITH_DISPOSABLE_TOKEN,
    payload: {
        productCode,
        isBilling,
        dashboardUrl,
        accountDashPathName,
        goToLegacyDash,
    },
});

export const PurchasePrivateIP = (params: { region: string; quantity: number }): Types.PurchasePrivateIPAction => ({
    type: AccountActionTypes.PURCHASE_PRIVATE_IP,
    payload: {
        region: params.region,
        quantity: params.quantity,
    },
});
export const AccountEditMarketingOptionsStart = (options: MarketingOptions[]): Types.AccountUpdateMarketingOptionsStartAction => ({
    type: AccountActionTypes.UPDATE_MARKETING_OPTIONS_START,
    payload: {
        options,
    },
});

export const AccountEditMarketingOptionsSuccess = (account: AccountPayload): Types.AccountUpdateMarketingOptionsSuccessAction => ({
    type: AccountActionTypes.UPDATE_MARKETING_OPTIONS_SUCCESS,
    payload: {
        account,
    },
});

export const UpdateCRCFormParam = (CRCParam: CrcFormPayload): Types.UpdateCrcFormParamAction => ({
    type: AccountActionTypes.UPDATE_CRC_PARAM,
    payload: {
        CRCParam,
    },
});
export const SendCrcForm = (): Types.SendCrcFormAction => ({
    type: AccountActionTypes.SEND_CRC,
    meta: {
        thunk: true,
    },
});
export const CrcFormSuccess = (meta: Record<string, any>): Types.CrcFormSuccessAction => ({
    type: AccountActionTypes.CRC_SUCCESS,
    meta,
});
export const CrcFormFailure = (meta: Record<string, any>): Types.CrcFormFailureAction => ({
    type: AccountActionTypes.CRC_FAILED,
    meta,
    error: true,
});

export const GetAccountsNameStart = (subaccountEmail?: string): Types.GetAccountsNameAction => ({
    type: AccountActionTypes.GET_ACCOUNTS_NAME,
    payload: {
        subaccountEmail,
        nameLoading: true,
    },
});

export const GetAccountsNameSuccess = (CrcProfile: CrcProfile): Types.GetAccountsNameSuccessAction => ({
    type: AccountActionTypes.GET_ACCOUNTS_NAME_SUCCESS,
    payload: {
        CrcProfile,
        nameLoading: false,
    },
});

export const GetAccountsNameError = (): Types.GetAccountsNameErrorAction => ({
    type: AccountActionTypes.GET_ACCOUNTS_NAME_ERROR,
    payload: {
        nameLoading: false,
    },
});

export const CrcValidate = (crcDecision: CrcDecision, subaccountEmail?: string): Types.CrcValidateAction => ({
    type: AccountActionTypes.CRC_VALIDATION,
    payload: {
        crcDecision,
        subaccountEmail,
    },
    meta: {
        thunk: true,
    },
});

export const CrcValidateSuccess = (meta: Record<string, any>): Types.CrcValidateSuccessAction => ({
    type: AccountActionTypes.CRC_VALIDATION_SUCCESS,
    meta,
});

export const CrcValidateFailure = (meta: Record<string, any>): Types.CrcValidateFailureAction => ({
    type: AccountActionTypes.CRC_VALIDATION_FAILURE,
    meta,
    error: true,
});

export const DeleteAccount = (password: string, reason?: Record<string, any>): Types.DeleteAccountAction => ({
    type: AccountActionTypes.DELETE_ACCOUNT,
    payload: {
        password,
        reason,
    },
});

export const GetAccountWarnings = (): Types.GetAccountWarningsAction => ({
    type: AccountActionTypes.GET_ACCOUNT_WARNINGS,
});

export const GetAccountWarningsSuccess = (warnings: AccountWarnings): Types.GetAccountWarningsSuccessAction => ({
    type: AccountActionTypes.GET_ACCOUNT_WARNINGS_SUCCESS,
    payload: {
        warnings,
    },
});

export const UpdateCrcStatusInAccount = (CrcStatus: CRCStatus): Types.UpdateCRCStatusInAccount => ({
    type: AccountActionTypes.UPDATE_CRC_STATUS_IN_ACCOUNT,
    payload: {
        CrcStatus,
    },
});

export const AccountGetCustomBrandingStart = (): Types.AccountGetCustomBrandingStartAction => ({
    type: AccountActionTypes.GET_CUSTOM_BRANDING_START,
});
export const AccountGetCustomBrandingSuccess = (brandingInfo: BrandingInfo): Types.AccountGetCustomBrandingSuccessAction => ({
    type: AccountActionTypes.GET_CUSTOM_BRANDING_SUCCESS,
    payload: {
        brandingInfo,
    },
});
export const AccountGetCustomBrandingError = (): Types.AccountGetCustomBrandingErrorAction => ({
    type: AccountActionTypes.GET_CUSTOM_BRANDING_ERROR,
});

export const PurchaseExtendedLogs = (): Types.PurchaseExtendedLogsAction => ({
    type: AccountActionTypes.PURCHASE_EXTENDED_LOGS,
});

export const SetValidSenderDomains = (ValidSenderDomainsOnly: boolean): Types.SetValidSenderDomainsAction => ({
    type: AccountActionTypes.SET_VALID_SENDER_DOMAINS_ONLY,
    payload: {
        ValidSenderDomainsOnly,
    },
    meta: {
        thunk: true,
    },
});

export const SetValidSenderDomainsError = (meta: Record<string, unknown>): Types.SetValidSenderDomainsErrorAction => ({
    type: AccountActionTypes.SET_VALID_SENDER_DOMIANS_ONLY_ERROR,
    error: true,
    meta,
});

export const UpdateGeneralInfo = (GeneralInfo: AccountCore): Types.UpdateGeneralInfoAction => ({
    type: AccountActionTypes.UPDATE_GENERAL_INFO,
    payload: {
        GeneralInfo,
    },
});

export const UpdateAiToolsTextCount = (count: number): Types.UpdateAiToolsTextCountAction => ({
    type: AccountActionTypes.UPDATE_OVERVIEW_AI_TOOLS_TEXT_COUNT,
    payload: {
        count,
    },
});

export const getICData = (store: ApplicationState) => store?.account?.IC || {};
export const getAccountStore = (store: ApplicationState): Types.AccountState => store.account;
export const getGeneralInfo = (store: ApplicationState): AccountCore => store?.account?.GeneralInfo || {};
export const getAccessLevels = (store: ApplicationState): AccessLevel[] => store?.account?.GeneralInfo?.AccountAccessLevel || [];
export const getAccountAdvancedOptions = (store: ApplicationState) => store?.account?.AdvancedOptions || {};
export const getAccountOverview = (store: ApplicationState): AccountOverview => store.account.Overview || {};
export const getAccountLimits = (store: ApplicationState) => store?.account?.Limits || {};
export const getGoogleAdTags = (store: ApplicationState) => store?.account?.GeneralInfo?.GoogleAdTags || [];
export const getPublicAccountId = (store: ApplicationState) => store?.account?.GeneralInfo?.PublicAccountID;
